import React, { useEffect, useRef, useState } from 'react';
import api from "../../../services/api";
import './Form.css';

const ContactForm = ({ cmsData, onSuccess }) => {
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const [turnstileStatus, setTurnstileStatus] = useState(turnstileSiteKey ? 'loading' : 'disabled');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    if (!turnstileSiteKey) return undefined;

    const renderTurnstile = () => {
      if (!window.turnstile || !turnstileRef.current || turnstileWidgetId.current !== null) return;

      try {
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: turnstileSiteKey,
          theme: 'light',
          callback: (token) => {
            setTurnstileToken(token);
            setTurnstileStatus('ready');
          },
          'expired-callback': () => setTurnstileToken(''),
          'error-callback': () => {
            setTurnstileToken('');
            setTurnstileStatus('failed');
          },
        });
        setTurnstileStatus('ready');
      } catch (error) {
        setTurnstileStatus('failed');
      }
    };

    const existingScript = document.querySelector('script[data-turnstile-script]');
    if (existingScript) {
      existingScript.addEventListener('load', renderTurnstile);
      renderTurnstile();
    } else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.turnstileScript = 'true';
      script.onload = renderTurnstile;
      script.onerror = () => setTurnstileStatus('failed');
      document.head.appendChild(script);
    }

    const timeout = window.setTimeout(() => {
      if (turnstileWidgetId.current === null) setTurnstileStatus('failed');
    }, 8000);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [turnstileSiteKey]);

  const formatTelefone = (value) => {
    const rawDigits = value.replace(/\D/g, '').slice(0, 11);
    
    if (rawDigits.length <= 2) {
      return rawDigits ? `(${rawDigits}` : '';
    }
    if (rawDigits.length <= 6) {
      return `(${rawDigits.slice(0, 2)}) ${rawDigits.slice(2)}`;
    }
    if (rawDigits.length <= 10) {
      return `(${rawDigits.slice(0, 2)}) ${rawDigits.slice(2, 6)}-${rawDigits.slice(6)}`;
    }
    return `(${rawDigits.slice(0, 2)}) ${rawDigits.slice(2, 7)}-${rawDigits.slice(7)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    const finalValue = name === 'phone' ? formatTelefone(value) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }

    if (apiError) {
      setApiError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');

    // Validação Front-end
    const errors = {};
    if (!formData.name.trim()) errors.name = "O nome é obrigatório.";

    if (!formData.email.trim()) {
      errors.email = "O e-mail é obrigatório.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "E-mail deve ser válido.";
    }
    
    const rawTelefone = formData.phone.replace(/\D/g, '');
    if (!rawTelefone) {
      errors.phone = "O telefone é obrigatório.";
    } else if (rawTelefone.length < 10) {
      errors.phone = "Insira um telefone válido com DDD.";
    }

    if (!formData.subject) errors.subject = "Selecione um assunto.";
    if (!formData.message.trim()) {
      errors.message = "A mensagem é obrigatória.";
    } else if (formData.message.trim().length < 10) {
      errors.message = "A mensagem deve ter pelo menos 10 caracteres.";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return; 
    }

    if (turnstileStatus === 'ready' && !turnstileToken) {
      setApiError('Confirme que você não é um robô para enviar a mensagem.');
      return;
    }

    setFormErrors({});
    setIsSubmitting(true);

    // Integração com a API
    try {
      await api.post('/contact', { ...formData, turnstileToken });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setTurnstileToken('');
      if (turnstileWidgetId.current !== null && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
      
      if (onSuccess) onSuccess();

    } catch (error) {
      console.error("Erro ao enviar formulário:", error);
      const backendErrors = error.response?.data?.errors;
      if (backendErrors && Array.isArray(backendErrors)) {
        const mappedErrors = {};
        backendErrors.forEach(err => { mappedErrors[err.field] = err.message; });
        setFormErrors(mappedErrors);
      } else {
        const errorMessage = error.response?.data?.error || "Não foi possível enviar sua mensagem no momento. Tente novamente mais tarde.";
        setApiError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="contato-form" onSubmit={handleSubmit}>
      <div className="input-group">
        <label htmlFor="name">{cmsData.textosFormulario.nameLabel}</label>
        <input 
          type="text" 
          id="name" 
          name="name" 
          placeholder={cmsData.textosFormulario.namePlaceholder} 
          value={formData.name} 
          onChange={handleChange}
          className={formErrors.name ? 'input-error' : ''} 
        />
        {formErrors.name && <span className="error-message">{formErrors.name}</span>}
      </div>

      <div className="input-group">
        <label htmlFor="email">{cmsData.textosFormulario.emailLabel}</label>
        <input 
          type="email" 
          id="email" 
          name="email" 
          placeholder={cmsData.textosFormulario.emailPlaceholder} 
          value={formData.email} 
          onChange={handleChange}
          className={formErrors.email ? 'input-error' : ''} 
        />
        {formErrors.email && <span className="error-message">{formErrors.email}</span>}
      </div>

      <div className="input-group">
        <label htmlFor="phone">{cmsData.textosFormulario.phoneLabel}</label>
        <input 
          type="tel" 
          id="phone" 
          name="phone" 
          placeholder={cmsData.textosFormulario.phonePlaceholder} 
          value={formData.phone} 
          onChange={handleChange}
          maxLength={15}
          className={formErrors.phone ? 'input-error' : ''} 
        />
        {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
      </div>

      <div className="input-group">
        <label htmlFor="subject">{cmsData.textosFormulario.subjectLabel}</label>
        <select 
          id="subject" 
          name="subject" 
          value={formData.subject} 
          onChange={handleChange}
          className={formErrors.subject ? 'input-error' : ''}
        >
          <option value="" disabled>{cmsData.textosFormulario.subjectLabel}</option>
          {cmsData.opcoesAssunto.map((opcao) => (
            <option key={opcao.id || opcao._id || opcao.valor} value={opcao.valor}>
              {opcao.rotulo}
            </option>
          ))}
        </select>
        {formErrors.subject && <span className="error-message">{formErrors.subject}</span>}
      </div>

      <div className="input-group">
        <label htmlFor="message">{cmsData.textosFormulario.messageLabel}</label>
        <textarea 
          id="message" 
          name="message" 
          rows="6" 
          placeholder={cmsData.textosFormulario.messagePlaceholder} 
          value={formData.message} 
          onChange={handleChange}
          className={formErrors.message ? 'input-error' : ''}
        ></textarea>
        {formErrors.message && <span className="error-message">{formErrors.message}</span>}
      </div>

      {turnstileSiteKey && (
        <div className="contact-turnstile" ref={turnstileRef} aria-live="polite">
          {turnstileStatus === 'failed' && (
            <span className="contact-turnstile__fallback">Validação automática indisponível. Você ainda pode enviar sua mensagem.</span>
          )}
        </div>
      )}

      {apiError && (
        <div className="api-error-message">
          {apiError}
        </div>
      )}

      <button 
        type="submit" 
        className={`btn-enviar ${isSubmitting ? 'loading' : ''}`}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Enviando...' : cmsData.textosFormulario.botaoEnviar}
      </button>
    </form>
  );
};

export default ContactForm;